/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React from 'react';
import {
    object, shape, string, func,
} from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Query } from 'react-apollo';
import mbpLogger from 'mbp-logger';
import QueryString from 'qs';
import { compose } from 'recompose';
import Media from 'react-media';
import { bindActionCreators } from 'redux';
// components
import PLASkeleton from '../GraphqlSkeletonComponents/PLASkeleton';
import PDPSkeleton from '../GraphqlSkeletonComponents/PDPSkeleton';
import MobilePDPSkeleton from '../GraphqlSkeletonComponents/MobileSkeleton/MobilePDPSkeleton';
import GraphqlProductPageBuilder from './GraphqlProductPageBuilder';
import GraphqlPlantsProductPageBuilder from './GraphqlPlantsProductPageBuilder';
import RedirectWithStatus from '../GraphqlCommonComponents/RedirectWithStatus/RedirectWithStatus';
// redux, hooks, helpers
import { GRAPHQL_ENV } from '../../../gql';
import findProductPageByPartNumber from '../../../gql/queries/findProductPageByPartNumber';
import isProductListingAd from '../../../helpers/isProductListingAd';
import * as app from '../../../../state/ducks/App';
import { getFeatureFlags, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { coreBrowsingPageViewSuccessTest } from '../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../helpers/tracking/hocs/withPageView';
import ProductNotAvailableContainer from './Partials/ProductNotAvailable/ProductNotAvailableContainer';
import ProductPageContainerWithPassport from './GraphqlProductPageContainerWithPassport';
import { setPassportBundleData } from '../../../../state/ducks/Checkout/ducks/Payment/ducks/Passport/Passport-Action';
import { getCurrencyData } from '../../../../state/ducks/App/App-Selectors';

const brandSelectors = app.ducks.brand.selectors;

const GraphqlProductPageContainer = ({
    location, brand, page, page: { code }, featureFlags, presentation_family, setBundleData, currencyData,
}) => {
    const PRODUCT_PAGE_QUERY = findProductPageByPartNumber(brand.domain, code, currencyData.currencyName);
    const urlParams = QueryString.parse(location.search);
    const baseProductPartNumber = QueryString.parse(location.search.replace(/^\?/, ''));
    const isPlaEnabled = featureFlags['is-pla-flow-enabled'];
    return (
        <Query fetchPolicy="cache-and-network" query={PRODUCT_PAGE_QUERY}>
            {({ loading, error, data }) => {
                if (loading) {
                    if (isPlaEnabled && isProductListingAd(location.search)) {
                        return <PLASkeleton />;
                    }
                    return (
                        <Media
                            query="(min-width: 599px)"
                        >
                            {(matches) => (matches
                                ?  <PDPSkeleton />
                                :  <MobilePDPSkeleton />

                            )}
                        </Media>
                    );
                }
                if (error) {
                    mbpLogger.logError({
                        query: PRODUCT_PAGE_QUERY,
                        component: 'GraphqlProductPageContainer.js',
                        message: 'Error executing query findProductPageByPartNumber',
                        env: GRAPHQL_ENV,
                        error,
                    });
                    return null;
                }
                if (!data) {
                    mbpLogger.logWarning({
                        query: PRODUCT_PAGE_QUERY,
                        component: 'GraphqlProductPageContainer.js',
                        message: 'No data returned for query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }
                if (GRAPHQL_ENV === 'development') {
                    console.log('Product Page Data ----->', data);
                }
                const {
                    product, defaultContent, content, isPassportBundleItem,
                } = data.findProductPageByPartNumber;
                const seoData = content?.entries?.[0]?.seo ? content.entries[0].seo : {};
                // GB42-636: Override seo_searchable_flag with false on product level for .CA site
                if (brand?.code === 'CAN') {
                    // Preventing empty seo_searchable value from Content Stack
                    if (seoData) {
                        seoData.seo_searchable = false;
                    }
                    if (product?.seo?.canonicalUrl) {
                        product.seo.canonicalUrl = '';
                    }
                }

                if (!data?.findProductPageByPartNumber) {
                    return <ProductNotAvailableContainer brand={brand} productUnavailableName={null} seoData={seoData} />;
                }
                // if no product data or product data doesnt exsist render best sellers
                if (!product || !product.availability || !product.productSkus?.length > 0) {
                    const productUnavailableName = content?.entries?.[0]?.name || null;
                    // client side - will it suffice for local development / uat ?
                    // occasionally if a product is cached in a category/collection/content page there will be a brief time the product will show but be unavailable
                    if (typeof window !== 'undefined' && window.location.host) {
                        return <ProductNotAvailableContainer brand={brand} productUnavailableName={productUnavailableName} seoData={seoData} />;
                    }
                    // We want to serve a 404 as per Merkle(SEO) for when a products not available
                    return <RedirectWithStatus to={location.pathname} status="404" productUnavailableName={productUnavailableName} />;
                    // return <ProductNotAvailableContainer brand={brand} productUnavailableName={productUnavailableName} />;
                }

                // url param for isPassportBundleProduct
                const regex = new RegExp(/\?pb=true/g);

                const passportBundle = content?.entries?.[0]?.passport_bundle || {};
                let bundlePromotionValid = true;
                if (passportBundle?.promotion_date_range?.to_date) {
                    const now = Date.now();
                    const toDate = new Date(passportBundle.promotion_date_range.to_date);
                    if (now > toDate) bundlePromotionValid = false;
                }
                if (passportBundle?.promotion_date_range?.from_date) {
                    const now = Date.now();
                    const fromDate = new Date(passportBundle.promotion_date_range.from_date);
                    if (now < fromDate) bundlePromotionValid = false;
                }
                if (location?.search && regex.test(location.search) && passportBundle?.check_to_enable_passport_bundle && bundlePromotionValid) {
                    return (
                        <ProductPageContainerWithPassport
                            content={defaultContent}
                            productBaseContent={content}
                            seoData={seoData}
                            product={product}
                            urlParams={urlParams}
                            page={page}
                            isPlaEnabled={isPlaEnabled}
                            isPla={isProductListingAd(location.search)}
                            presentation_family={presentation_family}
                            isPassportBundleItem={isPassportBundleItem || false} // If this value comes back as undefined in the PDP call default to false
                            baseProductPartNumber={baseProductPartNumber?.baseProductPartNumber || null}
                            isProductListingAd={isProductListingAd}
                        />
                    );
                }

                setBundleData({
                    enabled: false,
                });
                mbpLogger.logInfo({
                    message: 'data returning from PDP query',
                });
                // Render Plants Standard PDP Layout
                if (brand.code === 'PLA') {
                    const isProductListingPlants = (locationSearch) => {
                        const urlParam = new URLSearchParams(locationSearch);
                        const adidValue = urlParam.get('adid');
                        const adtypeValue = urlParam.get('adtype');
                        return adtypeValue === 'pla' ? `${product?.brandId}-${adidValue ?? ''}` : '';
                    };
                    const restrictedStates = data?.findProductPageByPartNumber?.content?.entries?.[0]?.additional_information?.[0]?.restrict_state;
                    return (
                        <GraphqlPlantsProductPageBuilder
                            content={defaultContent}
                            productBaseContent={content}
                            seoData={seoData}
                            product={product}
                            urlParams={urlParams}
                            page={page}
                            isPlaEnabled={isPlaEnabled}
                            plaSku={isProductListingPlants(location.search?.slice(1))}
                            presentation_family={presentation_family}
                            isPassportBundleItem={isPassportBundleItem || false} // If this value comes back as undefined in the PDP call default to false
                            baseProductPartNumber={baseProductPartNumber?.baseProductPartNumber || null}
                            // data={data}
                            restrictedStates={restrictedStates}
                            isProductListingAd={isProductListingAd}
                        />
                    );
                }
                // Render Standard PDP Layout
                return (
                    <div id="product_page_container">
                        <GraphqlProductPageBuilder
                            content={defaultContent}
                            productBaseContent={content}
                            seoData={seoData}
                            product={product}
                            urlParams={urlParams}
                            page={page}
                            isPlaEnabled={isPlaEnabled}
                            isPla={isProductListingAd(location.search)}
                            presentation_family={presentation_family}
                            isPassportBundleItem={isPassportBundleItem || false} // If this value comes back as undefined in the PDP call default to false
                            baseProductPartNumber={baseProductPartNumber?.baseProductPartNumber || null}
                        />
                    </div>
                );
            }}
        </Query>
    );
};

GraphqlProductPageContainer.propTypes = {
    brand: object.isRequired,
    page: shape({
        code: string,
    }).isRequired,
    location: object.isRequired,
    featureFlags: object.isRequired,
    presentation_family: string.isRequired,
    setBundleData: func.isRequired,
    currencyData: string.isRequired,
};

const mapStateToProps = (state) => ({
    brand: brandSelectors.getBrand(state),
    featureFlags: getFeatureFlags(state),
    presentation_family: getPresentationFamily(state),
    currencyData: getCurrencyData(state),
});

const mapDispatchToProps = (dispatch) => ({
    setBundleData: bindActionCreators(setPassportBundleData, dispatch),
});

const enhance = compose(
    withPageView({
        pageViewSuccessTest: coreBrowsingPageViewSuccessTest,
    }),
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GraphqlProductPageContainer);
